import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import InstallPWA from './components/InstallPWA';
import AppRuntimeStatus from './components/AppRuntimeStatus';
import LoadingScreen from './components/LoadingScreen';
import ToastContainer from './components/ToastContainer';
import { useToast } from './hooks/useToast';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Lazy loading para melhor performance
const Login = lazy(() => import('./pages/Login'));
const Cadastro = lazy(() => import('./pages/Cadastro'));
const CarteirinhaPublica = lazy(() => import('./pages/CarteirinhaPublica'));
const AlunoPortal = lazy(() => import('./pages/alunoPortal/AlunoPortal'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Alunos = lazy(() => import('./pages/Alunos'));
const Presencas = lazy(() => import('./pages/Presencas'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Fichas = lazy(() => import('./pages/Fichas'));
const FichasTemplateEditor = lazy(() => import('./pages/FichasTemplateEditor'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const Admin = lazy(() => import('./pages/Admin'));
const DiasGem = lazy(() => import('./pages/DiasGem'));
const Comuns = lazy(() => import('./pages/Comuns'));
const Auditoria = lazy(() => import('./pages/Auditoria'));
const Observabilidade = lazy(() => import('./pages/Observabilidade'));
const Backup = lazy(() => import('./pages/Backup'));
const Alertas = lazy(() => import('./pages/Alertas'));
const Integracoes = lazy(() => import('./pages/Integracoes'));
const AlertasWhatsApp = lazy(() => import('./pages/AlertasWhatsApp'));
const ConfiguracaoAptidao = lazy(() => import('./pages/ConfiguracaoAptidao'));
const MetasPedagogicas = lazy(() => import('./pages/MetasPedagogicas'));
const PainelPedagogico = lazy(() => import('./pages/PainelPedagogico'));
const AcompanhamentosPedagogicos = lazy(() => import('./pages/AcompanhamentosPedagogicos'));
const AlertasPedagogicos = lazy(() => import('./pages/AlertasPedagogicos'));
const AlertasAptidao = lazy(() => import('./pages/AlertasAptidao'));
const Instrutores = lazy(() => import('./pages/Instrutores'));
const InstrutorPresencas = lazy(() => import('./pages/InstrutorPresencas'));
const InstrutorRelatorios = lazy(() => import('./pages/InstrutorRelatorios'));
const InstrutorConfiguracoes = lazy(() => import('./pages/InstrutorConfiguracoes'));

function AppContent() {
  const { toasts, hideToast } = useToast();

  return (
    <>
      <Router>
        <div className="App">
          <AppRuntimeStatus />
          <InstallPWA />
          <ErrorBoundary>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Cadastro />} />
              <Route path="/carteirinha/q/:token" element={<CarteirinhaPublica />} />
              <Route path="/aluno" element={<AlunoPortal />} />
              <Route
                path="/"
                element={
                  <PrivateRoute requiredPermission="dashboard.view">
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/alunos"
                element={
                  <PrivateRoute requiredPermission="alunos.view">
                    <Alunos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/presencas"
                element={
                  <PrivateRoute requiredPermission="presencas.view">
                    <Presencas />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dias-gem"
                element={
                  <PrivateRoute requiredPermission="configuracoes.dias_gem.manage">
                    <DiasGem />
                  </PrivateRoute>
                }
              />
              <Route
                path="/comuns"
                element={
                  <PrivateRoute requiredPermission="configuracoes.dias_gem.manage">
                    <Comuns />
                  </PrivateRoute>
                }
              />
              <Route
                path="/auditoria"
                element={
                  <PrivateRoute requiredPermission="auditoria.view">
                    <Auditoria />
                  </PrivateRoute>
                }
              />
              <Route
                path="/observabilidade"
                element={
                  <PrivateRoute requiredPermission="observabilidade.view">
                    <Observabilidade />
                  </PrivateRoute>
                }
              />
              <Route
                path="/backup"
                element={
                  <PrivateRoute requiredPermission="backup.manage">
                    <Backup />
                  </PrivateRoute>
                }
              />
              <Route
                path="/alertas"
                element={
                  <PrivateRoute requiredPermission="alertas.sistema.view">
                    <Alertas />
                  </PrivateRoute>
                }
              />
              <Route
                path="/configuracoes/integracoes"
                element={
                  <PrivateRoute requiredPermission="configuracoes.integracoes.manage">
                    <Integracoes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/configuracoes/aptidao"
                element={
                  <PrivateRoute requiredPermission="configuracoes.aptidao.manage">
                    <ConfiguracaoAptidao />
                  </PrivateRoute>
                }
              />
              <Route
                path="/configuracoes/metas-pedagogicas"
                element={
                  <PrivateRoute requiredPermission="metas_pedagogicas.view">
                    <MetasPedagogicas />
                  </PrivateRoute>
                }
              />
              <Route
                path="/alertas-aptidao"
                element={
                  <PrivateRoute requiredPermission="alertas.aptidao.view">
                    <AlertasAptidao />
                  </PrivateRoute>
                }
              />
              <Route
                path="/alertas-whatsapp"
                element={
                  <PrivateRoute requiredPermission="alertas.whatsapp.view">
                    <AlertasWhatsApp />
                  </PrivateRoute>
                }
              />
              <Route
                path="/relatorios"
                element={
                  <PrivateRoute requiredPermission="relatorios.view">
                    <Relatorios />
                  </PrivateRoute>
                }
              />
              <Route
                path="/painel-pedagogico"
                element={
                  <PrivateRoute requiredPermission="relatorios.view">
                    <PainelPedagogico />
                  </PrivateRoute>
                }
              />
              <Route
                path="/acompanhamentos-pedagogicos"
                element={
                  <PrivateRoute requiredPermission="acompanhamentos.view">
                    <AcompanhamentosPedagogicos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/alertas-pedagogicos"
                element={
                  <PrivateRoute requiredPermission="alertas_pedagogicos.view">
                    <AlertasPedagogicos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/instrutores"
                element={
                  <PrivateRoute requiredPermission="instrutores.view">
                    <Instrutores />
                  </PrivateRoute>
                }
              />
              <Route
                path="/instrutores/presencas"
                element={
                  <PrivateRoute requiredPermission="instrutores.presencas.view">
                    <InstrutorPresencas />
                  </PrivateRoute>
                }
              />
              <Route
                path="/instrutores/relatorios"
                element={
                  <PrivateRoute requiredPermission="instrutores.relatorios.view">
                    <InstrutorRelatorios />
                  </PrivateRoute>
                }
              />
              <Route
                path="/instrutores/configuracoes"
                element={
                  <PrivateRoute requiredPermission="instrutores.configuracoes.manage">
                    <InstrutorConfiguracoes />
                  </PrivateRoute>
                }
              />
              <Route
                path="/fichas"
                element={
                  <PrivateRoute requiredPermission="fichas.view">
                    <Fichas />
                  </PrivateRoute>
                }
              />
              <Route
                path="/fichas/editor"
                element={
                  <PrivateRoute requiredPermission="fichas.templates.manage">
                    <FichasTemplateEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/usuarios"
                element={
                  <PrivateRoute requiredPermission="usuarios.manage">
                    <Usuarios />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <PrivateRoute requiredPermission="admin.view">
                    <Admin />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
      </Router>
      <ToastContainer toasts={toasts} onClose={hideToast} />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
